import type { ReactNode } from 'react';
import {
  FundraisersModuleGate,
  FundraisersModuleProvider,
  holdsWriteSettings,
  type FundraisersFlavor,
} from '@givmo/fundraisers-module';
import { ORG_TIME_ZONE_SETTING, useOrgSettingChangeable } from '@givmo/org-console';
import {
  SOCIAL_PUBLISH_PERMISSION,
  SOCIAL_READ_PERMISSION,
  SOCIAL_SLUG,
  socialPath,
} from '@givmo/social';
import { useRequestAccessDoor } from '../../components/request-access-context';
import { useAuth } from '../../auth/auth-context';
import { useCharityConsole } from '../../console/charity-console-context';
import { SETTINGS_ROUTE } from '../../console/console-routes';
import { useActiveOrg } from '../../org/active-org-context';
import { NotFound } from '../../components/not-found';
import { useFundraisersNavigation } from './use-fundraisers-navigation';
import { CharitySocialComposer } from './social-composer';
import {
  getDonateWebBaseUrl,
  isCampaignGroupsEnabled,
  isFundraiserWizardEnabled,
  isOrgQrDesignEnabled,
  isSupporterFundraisersEnabled,
  readApiBaseUrl,
} from '../../lib/env';
import { assistantStreamHeaders } from '../../lib/api-client';
import { PRIVACY_URL } from '../../lib/constants';

/** The organization-level string that turns the assistant on for a charity (`WRITE_ASSISTANT_CONSENT`). */
const MANAGE_ASSISTANT_CONSENT = 'charity_admin:edit';

/**
 * The charity flavor. `readPermission` is the string the backend's `READ_FUNDRAISERS` capability
 * resolves to for this class — the module's Member catalog carries one string, so it equals the
 * write string today; the nav's root row (and the Donations borrow of it) reads `readPermission`,
 * the page's writes read `writePermission`, and a later split of the catalog moves one line here.
 */
export const CHARITY_FUNDRAISERS_FLAVOR: FundraisersFlavor = {
  readPermission: 'charity_fundraiser:write',
  writePermission: 'charity_fundraiser:write',
  // EMC-WEB1: the backend's `MANAGE_CAMPAIGNS` (the module's Owner tier) schedules and cancels emails.
  managePermission: 'charity_p2p:manage',
  recipientMode: 'self',
  createDescription: 'Create a fundraiser for your organization.',
  // FCM-WEB49 (D-FCM-55, decision 4): the line above the Donors tab's list, as Jonathan shortened it
  // (2026-10-22 09:10 PDT): a charity on this screen has already accepted the donor-data terms.
  donorsNote: 'Donors who chose to share their details with your organization are named here.',
  // FCM-WEB50 (D-FCM-64, decision 6): the line above the Messages tab's composer. A charity's messages
  // reach only the donors who shared with it (D-FCM-43), all of whom its list names, so it lists none of
  // Givmo's notes under the preview (no `messagesNotesLine`).
  messagesNote:
    'Only donors who chose to share their details with your organization get your messages.',
};

/** What saving the organization's QR design changes, in this host's words (FCM-WEB4). */
export function charityQrSaveScope(orgName: string): string {
  return `Applies to all ${orgName} QR codes: your fundraisers and your public page.`;
}

export function CharityFundraisersMount({ children }: { children: ReactNode }) {
  const { read, standings } = useCharityConsole();
  const org = useActiveOrg();
  const navigation = useFundraisersNavigation();
  const door = useRequestAccessDoor();
  const { user } = useAuth();
  const orgName = org.activeMembership?.name ?? 'Your organization';
  // "Change in Settings" beside a time zone only where Settings lets this person change it: read
  // for a holder of `write_settings` alone, and absent while settings are dark.
  const zoneChangeable = useOrgSettingChangeable(
    org.isReady ? org.activeCharityId : null,
    ORG_TIME_ZONE_SETTING,
    holdsWriteSettings(read.capabilities),
  );
  return (
    <FundraisersModuleGate {...navigation} renderNotFound={() => <NotFound />}>
      <FundraisersModuleProvider
        config={{
          orgRef: org.isReady ? org.activeCharityId : null,
          orgName,
          // The supporter tab is this host's presentation fact, read per render off its knob.
          flavor: {
            ...CHARITY_FUNDRAISERS_FLAVOR,
            supporterFundraisers: isSupporterFundraisersEnabled(),
          },
          permissions: read.permissions,
          capabilities: read.capabilities,
          qrDesign: isOrgQrDesignEnabled() ? { saveScope: charityQrSaveScope(orgName) } : null,
          // EMC-WEB7: New fundraiser opens the creation wizard, read per render off its knob.
          creationWizard: isFundraiserWizardEnabled(),
          // The wizard asks a campaign whether people raise in groups, read per render off its knob.
          campaignGroups: isCampaignGroupsEnabled(),
          // EMC-WEB3: the sending domain's set-up starts from the person's own email domain.
          viewerEmail: user?.email?.trim() || null,
          // EMC-WEB4: "Create Board Ask" shows on a campaign while the organization has Board on.
          boardAsk: standings?.board?.orgEnabled === true,
          // EMC-WEB10: the AI email designer, with the Emails pages (EMC-BE21 retired its own knob).
          // Offering the organization's assistant sheet is advisory and permissive (an unsettled read
          // offers it); the server refuses on its own.
          emailDesigner: {
            apiBaseUrl: readApiBaseUrl(),
            authHeaders: assistantStreamHeaders,
            canAcceptForOrg:
              read.permissions === null || read.permissions.includes(MANAGE_ASSISTANT_CONSENT),
            privacyPolicyUrl: PRIVACY_URL,
          },
          // A link from another of this person's organizations names a fundraiser that one holds: the
          // page offers to switch there (in this tab only, `org/active-org-storage.ts`).
          otherOrganizations: org.isReady
            ? {
                organizations: org.memberships
                  .filter((membership) => membership.charity_id !== org.activeCharityId)
                  .map((membership) => ({ orgRef: membership.charity_id, name: membership.name })),
                switchTo: org.setActiveCharityId,
              }
            : null,
          // EMC-WEB5: a campaign's Social tab, renting the Social module this console mounts. Writing a post
          // is Social's `social:publish` (advisory: an unsettled read offers it, and Social refuses on its
          // own); without it the tab offers Social's request door (D-EMC-10). The composer is Social's own
          // card (SMM-WEB5), embedded in the tab (frame 40).
          social: {
            composer: CharitySocialComposer,
            canPublish:
              read.permissions === null || read.permissions.includes(SOCIAL_PUBLISH_PERMISSION),
            canRead: read.permissions === null || read.permissions.includes(SOCIAL_READ_PERMISSION),
            postHref: (postId) => socialPath({ page: 'post', postId }),
            onRequestAccess:
              standings?.[SOCIAL_SLUG]?.held === true ? undefined : () => door.open(SOCIAL_SLUG),
            accessRequestPending: standings?.[SOCIAL_SLUG]?.accessRequest?.status === 'PENDING',
          },
          // The organization's time zone off the ONE standing read: new deadlines and emails are set in it.
          orgTimeZone: read.timeZone,
          // Where its time zone is changed, offered only where Settings lets this person change it.
          links: {
            donateBaseUrl: getDonateWebBaseUrl(),
            ...(zoneChangeable ? { orgSettings: SETTINGS_ROUTE } : {}),
          },
          onNavigate: navigation.onNavigate,
          testIdPrefix: 'charity-fundraisers',
        }}
      >
        {children}
      </FundraisersModuleProvider>
    </FundraisersModuleGate>
  );
}
